// Samples evenly-spaced frames from an already-loaded <video> element into small JPEG data URLs -
// same seek+draw technique as ThumbnailSelector's frame picker, reused here to give each timeline
// clip a real filmstrip (so the user can actually see where they are) instead of a single static
// frame. `onFrame(index, dataUrl)`, if given, fires as soon as each individual frame is ready -
// seeking through a whole clip can take a moment, and filling slots in one at a time instead of
// waiting for the entire strip is both more responsive and shows concretely what's still loading.
//
// Deliberately one single element, seeking sequentially (not several probes splitting the work
// concurrently, which was tried here at one point) - decoding the *same* source from more than one
// <video> at once made individual seeks meaningfully more likely to miss the 800ms deadline below
// (probably network/decode contention over the same underlying resource, worse for portions nowhere
// near a keyframe or not yet buffered), which surfaced as outright black frames - most visibly for
// newly-revealed content right after dragging a trim handle back out. One element at a time is
// slower but reliable.
export const generateFilmstrip = async (videoEl, { startMs, endMs, count = 6, width = 90, onFrame, isCancelled } = {}) => {
    if (!videoEl.videoWidth || !videoEl.videoHeight) return []

    const height = Math.round((width * videoEl.videoHeight) / videoEl.videoWidth)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    // Some browsers/codecs never fire "seeked" for a handful of the requested timestamps (e.g.
    // a near-duplicate seek, or a keyframe-only decode path) - without a hard timeout the whole
    // loop stalls forever after the first successful frame, which is what showed up as the
    // filmstrip rendering only a single thumbnail instead of a full strip. And "seeked" can fire
    // a frame before the decoded image is actually painted into the element, which otherwise
    // silently captures the previous frame - two rAF ticks after it fires reliably avoids that.
    const seekTo = (ms) => new Promise((resolve) => {
        let done = false
        const finish = () => {
            if (done) return
            done = true
            videoEl.removeEventListener('seeked', finish)
            clearTimeout(timeoutId)
            requestAnimationFrame(() => requestAnimationFrame(resolve))
        }
        videoEl.addEventListener('seeked', finish)
        videoEl.currentTime = Math.max(0, ms) / 1000
        const timeoutId = setTimeout(finish, 800)
    })

    const thumbs = []
    for (let i = 0; i < count; i++) {
        // isCancelled - true once a newer buildFilmstrip call has superseded this one (see
        // TimelineClip.vue's filmstripGeneration) - stops this run from still writing frames (or
        // wasting further seeks) once it's no longer the one that matters.
        if (isCancelled && isCancelled()) break
        const t = startMs + ((endMs - startMs) * i) / Math.max(count - 1, 1)
        await seekTo(t)
        if (isCancelled && isCancelled()) break
        try {
            ctx.drawImage(videoEl, 0, 0, width, height)
            const dataUrl = canvas.toDataURL('image/jpeg', 0.6)
            thumbs.push(dataUrl)
            if (onFrame) onFrame(i, dataUrl)
        } catch (e) {
            break
        }
    }
    return thumbs
}
